import React, { useEffect, useState, useRef } from "react";
import { useFormikContext, FastField } from "formik";
import { Row, Col, Form, InputGroup, Spinner } from "react-bootstrap";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { MecordColors } from "Constants/Color";
import { parse, format } from "date-fns";
import { fetchProductEnquiryOrderDropdown, fetchServiceEnquiryOrderDropdown } from "slices/enquiry/thunk";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { Axios } from "api/axios";
import { AppDispatch } from "index";
import { autocompleteDropdownStyles } from "Constants/Style";
import { getCurrentLocalDate } from "helpers/CurrentDate/getCurrentLocalDate";
import AutoModeIcon from '@mui/icons-material/AutoMode';
import ClearIcon from '@mui/icons-material/Clear';

interface QuotationDetailsSectionProps {
  type?: 'product' | 'service';
}

const QuotationDetailsSection: React.FC<QuotationDetailsSectionProps> = ({ type = 'product' }) => {
  const { values, setFieldValue, errors, touched, handleChange } = useFormikContext<any>();
  const isOrderCreated = Boolean(values.has_order || values.sales_order_service_id);
  const dispatch = useDispatch<AppDispatch>();
  const [enquiryOptions, setEnquiryOptions] = useState<any[]>([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState<any>(null);
  const [isLoadingEnquiries, setIsLoadingEnquiries] = useState(false);
  const [isGeneratingUid, setIsGeneratingUid] = useState(false);
  const hasInitializedDate = useRef(false);

  // Selector for company dropdown data
  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({
      companiesData: company.companyList || [],
    })
  );

  const { companiesData } = useSelector(selectCompanyData);

  // Convert date from yyyy-MMM-dd or YYYY-MM-DD to YYYY-MM-DD for input
  const convertToInputFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    try {
      // First, try parsing YYYY-MM-DD format (e.g., "2026-01-14") - for initial values
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const parsed = new Date(dateStr + "T00:00:00"); // Add time to avoid timezone issues
        if (!isNaN(parsed.getTime())) {
          return format(parsed, "yyyy-MM-dd");
        }
      }
      // Try parsing yyyy-MMM-dd format (e.g., "2026-Jan-14")
      let parsed = parse(dateStr, "yyyy-MMM-dd", new Date());
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "yyyy-MM-dd");
      }
      // Fallback: try parsing dd-MMM-yyyy format (e.g., "14-Jan-2026") for backward compatibility
      parsed = parse(dateStr, "dd-MMM-yyyy", new Date());
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "yyyy-MM-dd");
      }
      return "";
    } catch {
      return "";
    }
  };

  // Convert date from YYYY-MM-DD to yyyy-MMM-dd for storage (using local date)
  const convertToStorageFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    try {
      // Parse as local date to avoid timezone conversion
      const [year, month, day] = dateStr.split("-").map(Number);
      const parsed = new Date(year, month - 1, day); // month is 0-indexed
      if (isNaN(parsed.getTime())) return "";
      return format(parsed, "yyyy-MMM-dd");
    } catch {
      return "";
    }
  };

  // Fetch enquiry dropdown options
  useEffect(() => {
    const fetchEnquiryOptions = async () => {
      setIsLoadingEnquiries(true);
      try {
        const thunk = type === 'service' ? fetchServiceEnquiryOrderDropdown : fetchProductEnquiryOrderDropdown;
        const response = await dispatch(thunk()).unwrap();
        const options = response?.data?.map((enquiry: any) => ({
          value: enquiry.enquiry_product_id || enquiry.enquiry_service_id,
          label: enquiry.customer_name ? `${enquiry.enquiry_number} ${enquiry.customer_name}` : enquiry.enquiry_number,
          enquiry_number: enquiry.enquiry_number,
          customer_name: enquiry.customer_name,
          enquiry_product_id: enquiry.enquiry_product_id,
          enquiry_service_id: enquiry.enquiry_service_id
        })) || [];
        setEnquiryOptions(options);
      } catch (error) {
        console.error("Failed to fetch enquiry options:", error);
      } finally {
        setIsLoadingEnquiries(false);
      }
    };

    fetchEnquiryOptions();
  }, [dispatch, type]);

  // Ensure quotation_date is set if empty (for new forms)
  useEffect(() => {
    if (!hasInitializedDate.current && !values.quotation_date) {
      const todayDate = getCurrentLocalDate();
      const converted = convertToStorageFormat(todayDate);
      setFieldValue("quotation_date", converted);
      hasInitializedDate.current = true;
    }
  }, [values.quotation_date, setFieldValue]);

  // Populate selectedEnquiry from existing form values during edit mode
  useEffect(() => {
    // if (enquiryOptions.length > 0) {
      const existingEnquiryId = values.enquiry_product_id || values.enquiry_service_id;
      const existingEnquiryNumber = values.enquiry_reference_number;
      console.log("existingEnquiryId", existingEnquiryId);
      console.log("existingEnquiryNumber", existingEnquiryNumber);
      console.log("enquiryOptions", enquiryOptions);
      console.log("selectedEnquiry", selectedEnquiry);
      console.log("values", values);
      // Only proceed if we have an enquiry ID or number and haven't already set a matching enquiry
      if ((existingEnquiryId || existingEnquiryNumber) && !selectedEnquiry) {
        // Find matching enquiry from options
        const matchingEnquiry = enquiryOptions.find(
          (opt) =>
            (existingEnquiryId && (
              opt.enquiry_product_id === existingEnquiryId ||
              opt.enquiry_service_id === existingEnquiryId
            )) ||
            (existingEnquiryNumber && opt.enquiry_number === existingEnquiryNumber)
        );

        if (matchingEnquiry) {
          setSelectedEnquiry(matchingEnquiry);
        } else if (existingEnquiryNumber) {
          // If no match found but we have an enquiry number, create a placeholder option
          setSelectedEnquiry({
            value: existingEnquiryId || existingEnquiryNumber,
            label: existingEnquiryNumber,
            enquiry_number: existingEnquiryNumber,
            customer_name: "",
            enquiry_product_id: values.enquiry_product_id || null,
            enquiry_service_id: values.enquiry_service_id || null
          });
        }
      }
    // }
  }, [enquiryOptions.length, values.enquiry_product_id, values.enquiry_service_id, values.enquiry_reference_number, selectedEnquiry]);

  // Handle enquiry selection and auto-populate form
  const handleEnquiryChange = async (_: any, newValue: any) => {
    setSelectedEnquiry(newValue);
    if (newValue) {

      // Reset Assigned Members
      setFieldValue("members", []);
      

      // Set enquiry reference number
      setFieldValue("enquiry_reference_number", newValue.enquiry_number);

      // Determine enquiry type and set appropriate ID
      const enquiryId = newValue.enquiry_product_id || newValue.enquiry_service_id;
      const enquiryType = newValue.enquiry_product_id ? 'product' : 'service';

      if (enquiryType === 'product') {
        setFieldValue("enquiry_product_id", enquiryId);
      } else {
        // For service enquiries, we might need to store the service enquiry ID
        setFieldValue("enquiry_service_id", enquiryId);
      }

      // Fetch full enquiry details
      try {
        const apiEndpoint = enquiryType === 'product'
          ? API_ENDPOINT.ENQUIRY.PRODUCT.DETAIL(enquiryId)
          : API_ENDPOINT.ENQUIRY.SERVICE.DETAIL(enquiryId);

        const response = await Axios.get(apiEndpoint);

        if (response.data.success && response.data.data) {
          const enquiryData = response.data.data;
          const enquiryAttachments = Array.isArray(enquiryData.attachments)
            ? enquiryData.attachments
                .map((attachment: any) =>
                  typeof attachment === "string"
                    ? attachment
                    : attachment?.file_url || attachment?.url || "",
                )
                .filter(Boolean)
            : [];

          setFieldValue("attachments", enquiryAttachments);

          if (enquiryData.uid_number) {
            setFieldValue("uid_number", enquiryData.uid_number);
          }

          if (enquiryData.reference_number) {
            setFieldValue("reference_number", enquiryData.reference_number);
          }

          setFieldValue("initiated_by", enquiryData.initiated_by || "");

          // Auto-populate customer details
          if (enquiryData.customer_details) {
            // Find the company in the master list
            const masterCompany = companiesData.find(
              (c: any) => c.company_id === enquiryData.customer_details.company_id
            );
            const enquiryContacts =
              enquiryData.customer_details.contact_details ||
              enquiryData.customer_details.contacts ||
              [];

            if (masterCompany) {
              setFieldValue("company", masterCompany);
            } else {
              // Fallback to enquiry's customer details if not in master list
              setFieldValue("company", {
                company_id: enquiryData.customer_details.company_id,
                company_name: enquiryData.customer_details.company_name,
                gstin: enquiryData.customer_details.gstin,
                gst_type: enquiryData.customer_details.gst_type,
                industry: enquiryData.customer_details.industry,
                phone_number: enquiryData.customer_details.phone_number,
                company_email: enquiryData.customer_details.email,
                billing_address: enquiryData.customer_details.billing_address,
                shipping_addresses: enquiryData.customer_details.shipping_address,
                contacts: enquiryContacts.map((contact: any) => ({
                  id: contact.contact_id || contact.id,
                  contact_id: contact.contact_id || contact.id,
                  name: contact.name || contact.contact_person_name || contact.contact_name,
                  designation: contact.designation || contact.contact_person_designation,
                  phone_country: contact.phone_country || contact.contact_person_phone_country,
                  phone_number: contact.phone_number || contact.contact_person_phone_no,
                  email: contact.email || contact.contact_person_email,
                })),
              });
            }

            // Set contact persons from enquiry
            if (enquiryContacts.length > 0) {
              setFieldValue("contact_persons",
                enquiryContacts.map((contact: any) => ({
                  id: contact.contact_id || contact.id,
                  name: contact.name || contact.contact_person_name || contact.contact_name,
                  designation: contact.designation || contact.contact_person_designation,
                  phone_country: contact.phone_country || contact.contact_person_phone_country,
                  phone_number: contact.phone_number || contact.contact_person_phone_no,
                  email: contact.email || contact.contact_person_email,
                }))
              );
            }
          }

          // Auto-populate tags from enquiry if available
          if (Array.isArray(enquiryData.tags)) {
            setFieldValue(
              "tags",
              enquiryData.tags.map((t: any) => String(t).trim()).filter(Boolean),
            );
          }

          // Auto-populate agent name
          if (enquiryData.agent_name) {
            setFieldValue("agent_name", enquiryData.agent_name);
          }

          // Auto-populate organisation (bill from) from enquiry
          if (
            enquiryData.bill_from &&
            typeof enquiryData.bill_from === "object" &&
            !Array.isArray(enquiryData.bill_from) &&
            Object.keys(enquiryData.bill_from).length > 0
          ) {
            setFieldValue("bill_from_address", enquiryData.bill_from);
          }
          
          if (enquiryData.assigned_members && Array.isArray(enquiryData.assigned_members) && enquiryData.assigned_members.length > 0) {
            const members = enquiryData.assigned_members.map((member: any) => {
              console.log("member in quotation details section", member);
              const firstName = member.first_name || "";
              const lastName = member.last_name || "";
              const initials = (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
              return {
                id: member.user_id,
                name:`${member.first_name} ${member.last_name}`.trim() ||  member.username || `${firstName} ${lastName}`.trim() || "" ,
                initials,
                first_name: firstName,
                last_name: lastName,
              };
            });
            console.log("members in quotation details section", members);
            setFieldValue("members", members);
          }

          // Auto-populate assigned_technician from enquiry
          if (
            enquiryData.assigned_technician &&
            Array.isArray(enquiryData.assigned_technician) &&
            enquiryData.assigned_technician.length > 0
          ) {
            const technicians = enquiryData.assigned_technician.map((tech: any) => ({
              id: tech.user_id || tech.id,
              user_id: tech.user_id || tech.id,
              first_name: tech.first_name || "",
              last_name: tech.last_name || "",
              username: tech.username || "",
            }));
            setFieldValue("assigned_technician", technicians);
          }

          // Auto-populate products or services based on enquiry type
          if (enquiryType === 'product' && enquiryData.enquiry_product_items && enquiryData.enquiry_product_items.length > 0) {
            const products = enquiryData.enquiry_product_items.map((item: any, index: number) => ({
              id: `enquiry-product-${item.enquiry_product_item_id}`,
              sr_no: index + 1,
              // Backend mandatory fields
              product_id: item.product_id,
              product_name: item.product_name,
              category_name: item.category_name,
              sub_category_name: item.sub_category_name,
              product_type: item.product_type || [],
              make: item.make_name,
              make_name: item.make_name,
              model: item.model_name,
              model_name: item.model_name,
              unit: item.unit_name,
              unit_name: item.unit_name,
              unit_price: parseFloat(item.unit_price || "0"),
              hsn_code: item.hsn_code,
              gst_percentage: parseFloat(item.gst_percentage || "0"),
              gst_value: 0,
              remark: item.remark || "",
              sku: item.product_sku,
              product_sku: item.product_sku,
              quantity: item.product_quantity ?? 1,
              product_quantity: item.product_quantity ?? 1,
              product_total: parseFloat(item.product_total || "0"),
              product_net_total: parseFloat(item.product_total || "0"),
              discount_type: "currency",
              discount_value: 0,
              serviceable: true,
              is_serviceable: true,
              // Frontend specific fields
              discount: 0,
              net_value: parseFloat(item.product_total || "0"),
              total: parseFloat(item.product_total || "0"),
              shipping_address: null,
              rate_in_currency: parseFloat(item.unit_price || "0"),
              gross_value: parseFloat(item.product_total || "0"),
            }));

            setFieldValue("products", products);
          } else if (enquiryType === 'service' && enquiryData.enquiry_service_items && enquiryData.enquiry_service_items.length > 0) {
            // Helper function to round to 2 decimal places
            const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

            const services = enquiryData.enquiry_service_items.map((item: any, index: number) => {
              // Parse numeric values
              const unitPrice = parseFloat(item.service_rate || item.unit_price || "0");
              const quantity = item.product_quantity ?? 1;
              const gstPercent = parseFloat(item.gst_percentage || "0");

              // Account for Compositional GST type (will be set later when company is loaded)
              const companyGstType = values.company?.gst_type;
              const effectiveGstPercent = companyGstType === "Compositional gst" ? 0 : gstPercent;

              // Calculate values
              const grossValue = roundToTwoDecimals(unitPrice * quantity);
              const discountValue = 0; // Initial discount is 0
              const netValue = roundToTwoDecimals(grossValue - discountValue);
              const gstValue = roundToTwoDecimals((netValue * effectiveGstPercent) / 100);
              const total = roundToTwoDecimals(netValue + gstValue);

              let serviceAddressObject = null;
              if (item.shipping_address) {
                if (Array.isArray(item.shipping_address) && item.shipping_address.length > 0) {
                  const firstAddress = item.shipping_address[0];
                  if (typeof firstAddress === "object" && firstAddress !== null) {
                    serviceAddressObject = {
                      shipping_address_id: firstAddress.shipping_address_id || firstAddress.id,
                      company_id: firstAddress.company_id,
                      address: firstAddress.address,
                      city: firstAddress.city,
                      state: firstAddress.state,
                      country: firstAddress.country,
                      pincode: firstAddress.pincode || firstAddress.pin_code,
                      phone_number: firstAddress.phone_number,
                      phone_country: firstAddress.phone_country,
                      is_default: firstAddress.is_default || false,
                      created_at: firstAddress.created_at,
                      updated_at: firstAddress.updated_at,
                    };
                  } else if (typeof firstAddress === "string") {
                    serviceAddressObject = firstAddress;
                  }
                } else if (typeof item.shipping_address === "object" && item.shipping_address !== null) {
                  serviceAddressObject = {
                    shipping_address_id: item.shipping_address.shipping_address_id || item.shipping_address.id,
                    company_id: item.shipping_address.company_id,
                    address: item.shipping_address.address,
                    city: item.shipping_address.city,
                    state: item.shipping_address.state,
                    country: item.shipping_address.country,
                    pincode: item.shipping_address.pincode || item.shipping_address.pin_code,
                    phone_number: item.shipping_address.phone_number,
                    phone_country: item.shipping_address.phone_country,
                    is_default: item.shipping_address.is_default || false,
                    created_at: item.shipping_address.created_at,
                    updated_at: item.shipping_address.updated_at,
                  };
                } else if (typeof item.shipping_address === "string") {
                  serviceAddressObject = item.shipping_address;
                }
              }

              return {
                id: `${item.enquiry_service_item_id}`,
                product_id: item.product_id, // Set the actual product_id
                sr_no: index + 1,
                model: item.model_name,
                serviceName: item.product_name, // ServiceListSection expects serviceName
                serial_no: item.serial_no || "", // ServiceListSection expects serial_no
                serviceCategory: item.service_category, // ServiceListSection expects serviceCategory
                serviceMethod: item.service_method, // ServiceListSection expects serviceMethod
                serviceType: item.service_type, // ServiceListSection expects serviceType
                executionMode: item.execution_mode, // ServiceListSection expects executionMode
                product_quantity: quantity,
                quantity: quantity,
                productQuantity: quantity.toString(),
                // Set both unit_price and service_amount for compatibility
                unit_price: unitPrice,
                service_amount: unitPrice,
                serviceRate: unitPrice.toString(),
                // Calculated values
                gross_value: grossValue,
                discount: 0,
                discount_type: "currency",
                discount_value: discountValue,
                net_value: netValue,
                gst_percentage: effectiveGstPercent,
                gst_value: gstValue,
                total: total,
                total_price: total,
                total_service_amount: total,
                serviceAddress: serviceAddressObject, // ServiceListSection expects serviceAddress object
                reportedComplaint: item.reported_complaints || "", // ServiceListSection expects reportedComplaint
                // Additional fields from enquiry data
                category_name: item.category || "",
                sub_category_name: item.sub_category_name || "",
                make_name: item.make_name || "",
                unit_name: item.unit_name || "",
                hsn_code: item.hsn_code || "",
                sac: item.sac_code || item.sac || "",
                sac_code: item.sac_code || item.sac || "",
                sac_code_id: item.sac_code_id || item.sac_id || "",
                product_sku: item.product_sku || "",
                // Keep snake_case versions for backend compatibility
                service_category: item.service_category,
                service_method: item.service_method,
                service_type: item.service_type,
                execution_mode: item.execution_mode,
                service_address: serviceAddressObject,
                reported_complaint: item.reported_complaints || "",
              };
            });

            setFieldValue("services", services);
          }
        }
      } catch (error) {
        console.error("Error fetching enquiry details:", error);
        // Don't show error to user, just log it
      }
    } else {
      // Clear fields when enquiry is deselected
      setFieldValue("enquiry_product_id", "");
      setFieldValue("enquiry_service_id", "");
      setFieldValue("enquiry_reference_number", "");
      setFieldValue("reference_number", "");
      setFieldValue("initiated_by", "");
      setFieldValue("customer_details", null);
      setFieldValue("company", null);
      setFieldValue("contact_persons", []);
      setFieldValue("agent_name", "");
      setFieldValue("products", []);
      setFieldValue("services", []);
      setFieldValue("tags", []);
      setFieldValue("attachments", []);
    }
  };

  // Handle UID number generation
  const handleGenerateUid = async () => {
    setIsGeneratingUid(true);
    try {
      const response = await Axios.get('/order/uid-number/generate');
      if (response.data.success && response.data.data) {
        setFieldValue('uid_number', response.data.data);
      }
    } catch (error: any) {
      console.error('Error generating UID number:', error);
      // Optionally show error toast here
    } finally {
      setIsGeneratingUid(false);
    }
  };

  // Handle UID number clear
  const handleClearUid = () => {
    setFieldValue('uid_number', '');
  };

  const hasUidNumber = values.uid_number && values.uid_number.trim() !== '';

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-start mb-3">
        <h5 className="fw-semibold mb-2">
          Quotation Details
        </h5>
      </div>

      <Row
        className="g-3 pb-3 px-1 rounded"
        style={{ backgroundColor: MecordColors?.LightGrayColor }}
      >
        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>
              Quotation Date<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="quotation_date"
              value={convertToInputFormat(values.quotation_date || "")}
              disabled={isOrderCreated}
              onChange={(e) => {
                const converted = convertToStorageFormat(e.target.value);
                setFieldValue("quotation_date", converted);
              }}
              onBlur={() => {
                // Handle blur if needed
              }}
              isInvalid={!!(errors.quotation_date && touched.quotation_date)}
            />
            {errors.quotation_date && touched.quotation_date && (
              <Form.Control.Feedback type="invalid">
                {errors.quotation_date as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>
              Valid Until<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="valid_until"
              value={convertToInputFormat(values.valid_until || "")}
              disabled={isOrderCreated}
              onChange={(e) => {
                const converted = convertToStorageFormat(e.target.value);
                setFieldValue("valid_until", converted);
              }}
              onBlur={() => {
                // Handle blur if needed
              }}
              isInvalid={!!(errors.valid_until && touched.valid_until)}
            />
            {errors.valid_until && touched.valid_until && (
              <Form.Control.Feedback type="invalid">
                {errors.valid_until as string}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>Enquiry Reference Number</Form.Label>
            <Autocomplete
              disabled={isOrderCreated}
              options={enquiryOptions}
              value={selectedEnquiry}
              onChange={handleEnquiryChange}
              getOptionLabel={(option) => option?.enquiry_number || option?.label || ""}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return (
                  (option.enquiry_product_id && option.enquiry_product_id === value?.enquiry_product_id) ||
                  (option.enquiry_service_id && option.enquiry_service_id === value?.enquiry_service_id) ||
                  (option.enquiry_number && option.enquiry_number === value?.enquiry_number) ||
                  (option.value && option.value === value?.value)
                );
              }}
              loading={isLoadingEnquiries}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>
                      {option.enquiry_number} {option.customer_name}
                    </span>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select enquiry..."
                  size="small"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#afc0f7",
                      },
                    },
                  }}
                  error={!!(errors.enquiry_reference_number && touched.enquiry_reference_number)}
                />
              )}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
            />
            {errors.enquiry_reference_number && touched.enquiry_reference_number && (
              <div className="invalid-feedback d-block">{errors.enquiry_reference_number as string}</div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>Reference Number</Form.Label>
            <Form.Control
              type="text"
              name="reference_number"
              value={values.reference_number || ""}
              onChange={handleChange}
              placeholder="Enter Reference Number"
              readOnly={!!selectedEnquiry || isOrderCreated}
              disabled={!!selectedEnquiry || isOrderCreated}
            />
          </Form.Group>
        </Col>

        {/* UID Number field - only for service quotations */}
        {type === 'service' && (
          <Col xs={12} sm={6} md={6}>
            <Form.Group>
              <Form.Label>UID Number</Form.Label>
              <InputGroup style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  name="uid_number"
                  value={values.uid_number || ''}
                  readOnly
                  placeholder="Click icon to generate"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', paddingRight: '40px' }}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '0',
                    zIndex: 10,
                    cursor: isGeneratingUid ? 'not-allowed' : 'pointer',
                    pointerEvents: isGeneratingUid ? 'none' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={
                    isOrderCreated || isGeneratingUid
                      ? undefined
                      : hasUidNumber
                        ? handleClearUid
                        : handleGenerateUid
                  }
                >
                  {isGeneratingUid ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '18px', height: '18px', borderWidth: '2px' }}
                    />
                  ) : hasUidNumber ? (
                    <ClearIcon
                      style={{
                        fontSize: '18px',
                        color: MecordColors.SecondaryButtonTextColor,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <AutoModeIcon
                      style={{
                        fontSize: '18px',
                        color: MecordColors.PrimaryButtonBGColor,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                </div>
              </InputGroup>
              {errors.uid_number && touched.uid_number && (
                <div className="invalid-feedback d-block">{errors.uid_number as string}</div>
              )}
            </Form.Group>
          </Col>
        )}

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>Initiated By{type !== 'service' && <span className="text-danger">*</span>}</Form.Label>
            <FastField name="initiated_by">
              {({ field }: any) => (
                <Form.Control
                  {...field}
                  placeholder="Enter Initiated By"
                  disabled={isOrderCreated}
                />
              )}
            </FastField>
            {errors.initiated_by && touched.initiated_by && (
              <div className="invalid-feedback d-block">{errors.initiated_by as string}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default QuotationDetailsSection;
